import { useMemo, useReducer } from 'react';
import {
	GenericTableState,
	GenericTableAction_Type,
} from './@types/GenericTable.types';
import { GenericComponentData } from 'src/@types/common';

export const createInitialState = (
	defaultPreferences: Record<string, unknown>
): GenericTableState => ({
	filterText: '',
	sortingField: 'name',
	sortingDescending: false,
	currentPageIndex: 1,
	preferences: defaultPreferences || { pageSize: 10 },
});

export const genericTableReducer = (
	state: GenericTableState,
	action: GenericTableAction_Type
): GenericTableState => {
	switch (action.type) {
	case 'SET_FILTER_TEXT':
		return { ...state, filterText: action.payload, currentPageIndex: 1 };
	case 'SET_SORTING':
		return {
			...state,
			sortingField: action.payload.field,
			sortingDescending: action.payload.descending,
		};
	case 'SET_CURRENT_PAGE':
		return { ...state, currentPageIndex: action.payload };
	case 'SET_PREFERENCES':
		return { ...state, preferences: action.payload, currentPageIndex: 1 };
	default:
		return state;
	}
};

export const useGenericTable = <T extends GenericComponentData>(
	items: T[],
	filterFields: (keyof T | string)[] = [],
	defaultPreferences?: Record<string, unknown>
) => {
	const [state, dispatch] = useReducer(
		genericTableReducer,
		createInitialState(defaultPreferences || {})
	);

	const filteredItems = useMemo(() => {
		if (!state.filterText || filterFields.length === 0) {
			return items;
		}

		const lowerFilterText = state.filterText.toLowerCase();

		return items.filter((item) =>
			filterFields.some((field) => {
				const value = (item as GenericComponentData)[field as string];

				return value && String(value).toLowerCase().includes(lowerFilterText);
			})
		);
	}, [items, state.filterText, filterFields]);

	const sortedItems = useMemo(() => {
		return [...filteredItems].sort((a, b) => {
			const aValue = (a as GenericComponentData)[state.sortingField];
			const bValue = (b as GenericComponentData)[state.sortingField];

			if (typeof aValue === 'string' && typeof bValue === 'string') {
				return state.sortingDescending
					? bValue.localeCompare(aValue)
					: aValue.localeCompare(bValue);
			}

			if (typeof aValue === 'number' && typeof bValue === 'number') {
				return state.sortingDescending ? bValue - aValue : aValue - bValue;
			}

			return 0;
		});
	}, [filteredItems, state.sortingField, state.sortingDescending]);

	const paginatedItems = useMemo(() => {
		const startIndex = (state.currentPageIndex - 1) * (state.preferences.pageSize as number);
		const endIndex = startIndex + (state.preferences.pageSize as number);

		return sortedItems.slice(startIndex, endIndex);
	}, [sortedItems, state.currentPageIndex, state.preferences.pageSize]);

	const filterCounter = (count: number) => {
		return `${count} ${count === 1 ? 'match' : 'matches'}`;
	};

	return {
		state,
		dispatch,
		filteredItems,
		sortedItems,
		paginatedItems,
		filterCounter,
	};
};
